import * as React from 'react'

import { Box, BoxList, Spinner, Text } from '@factorialco/deprecated-design-system'
import { F0AvatarPerson, F0Box, F0TagStatus, F0Text } from '@factorialco/f0-react'

import buildUrl from 'modules/core/lib/buildUrl'
import i18n from 'modules/core/lib/i18n'

import StatusItem, {
  StatusStruct,
} from 'modules/documents/components/DocumentPreview/Content/StatusItem'
import SignatureStatusTag from 'modules/documents/components/Documents/List/Item/SignatureStatusTag'
import { Document } from 'modules/documents/hooks/documents/useGetDocuments'
import type {
  EsignatureRequest,
  Signee,
} from 'modules/esignatures/hooks/requests/useGetEsignatureRequests'

interface Props {
  document: Document
  loading: boolean
  additionalSignees: EsignatureRequest[]
  useF0?: boolean
}

const SignatureRequests = ({ document, loading, additionalSignees, useF0 = false }: Props) => {
  const employee = document.employee
  const toStatusStruct = (signee: Signee): StatusStruct => {
    return {
      signerFullName: signee.fullName,
      ...(signee.avatar && { signerAvatar: signee.avatar }),
      statusDescription: i18n.t('documents.modal.fillable.waiting_for', {
        employee: employee && employee.fullName,
      }),
    }
  }

  if (loading) {
    if (useF0) {
      return null
    }
    return (
      <Box alignItems="center">
        <Spinner />
      </Box>
    )
  }

  if (useF0) {
    return (
      <>
        {additionalSignees.length > 0 && (
          <F0Box gap="sm">
            <F0Text
              variant="description"
              content={i18n.t('documents.modal.fillable.additional_signers')}
            />
            <F0Box gap="xs">
              {additionalSignees.map((request) => (
                <F0Box
                  key={request.id}
                  display="flex"
                  padding="md"
                  border="default"
                  borderColor="default"
                  borderRadius="md"
                  flexDirection="row"
                  alignItems="center"
                  justifyContent="between"
                >
                  <F0Box display="flex" flexDirection="row" alignItems="center" gap="sm">
                    <F0AvatarPerson
                      src={buildUrl(request.signee.avatar)}
                      firstName={request.signee.fullName.split(' ')[0] || ''}
                      lastName={request.signee.fullName.split(' ').slice(1).join(' ') || ''}
                    />
                    <F0Box gap="xs">
                      <F0Text variant="body" content={request.signee.fullName} />
                      <F0Text
                        variant="description"
                        content={i18n.t('documents.modal.fillable.waiting_for', {
                          employee: employee && employee.fullName,
                        })}
                      />
                    </F0Box>
                  </F0Box>
                  <F0TagStatus
                    text={i18n.t('documents.modal.fillable.status.to_fill')}
                    variant="warning"
                  />
                </F0Box>
              ))}
            </F0Box>
          </F0Box>
        )}
      </>
    )
  }

  return (
    <>
      {additionalSignees.length > 0 && (
        <Box gap="s8">
          <Text size="100" color="grey800">
            {i18n.t('documents.modal.fillable.additional_signers')}
          </Text>
          <BoxList>
            {additionalSignees.map((request) => {
              return (
                <StatusItem
                  key={request.id}
                  status={toStatusStruct(request.signee)}
                  statusBox={<SignatureStatusTag status={'pending'} />}
                />
              )
            })}
          </BoxList>
        </Box>
      )}
    </>
  )
}

export default SignatureRequests
